import type { KeyboardEvent, ReactNode } from 'react'
import { ChevronDown, Clock3, Hash, Search, Tags } from 'lucide-react'

import type { DataGridColumnSchema, DataGridFilter } from '../lib/types'
import { cn } from '@/lib/utils'
import { Popover, PopoverTrigger } from '@/ui/popover'

import type { DataGridFilterPresentation } from './data-grid-filter-panel'

export type DataGridFilterPresentationProps = {
  column: DataGridColumnSchema
  presentation: DataGridFilterPresentation
  open?: boolean
  onBack?: () => void
  onApplied?: () => void
}

export function DataGridFilterChip({
  column,
  summary,
  open,
  onOpenChange,
  children,
}: {
  column: DataGridColumnSchema
  summary?: string
  open?: boolean
  onOpenChange?: (open: boolean) => void
  children: ReactNode
}) {
  const active = summary !== undefined
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger
        aria-label={`Filter by ${column.label.toLocaleLowerCase()}`}
        className={cn(
          'group border-input focus-visible:border-ring focus-visible:ring-ring/30 inline-flex h-7 shrink-0 items-center overflow-hidden rounded-md border text-xs outline-hidden transition-colors focus-visible:ring-2',
          active
            ? 'bg-background text-foreground shadow-xs hover:bg-muted'
            : 'text-muted-foreground hover:border-muted-foreground/40 hover:bg-muted hover:text-foreground border-dashed bg-transparent',
        )}
      >
        <span className="flex min-w-0 items-center gap-1.5 px-2">
          <DataGridFilterIcon kind={column.kind} className="size-3.5 shrink-0" />
          <span className="whitespace-nowrap">{column.label}</span>
        </span>
        {active ? (
          <span className="border-border bg-muted/60 text-foreground flex items-center self-stretch whitespace-nowrap border-l px-1.5 font-medium">
            {summary}
          </span>
        ) : (
          <ChevronDown aria-hidden className="mr-1.5 size-3 shrink-0 opacity-60" />
        )}
      </PopoverTrigger>
      {children}
    </Popover>
  )
}

export function dataGridFilterSummary(
  filter: DataGridFilter | undefined,
  column: DataGridColumnSchema,
): string | undefined {
  if (!filter) return undefined
  if (filter.operator === 'empty') return 'Empty'
  if (Array.isArray(filter.values)) {
    if (filter.values.length === 1) {
      return column.labels?.[filter.values[0]] ?? filter.values[0]
    }
    return `${filter.values.length} selected`
  }
  if (typeof filter.values === 'string') {
    return `${operatorLabel(filter.operator)} “${column.labels?.[filter.values] ?? filter.values}”`
  }
  if (typeof filter.values === 'number') {
    return `${operatorLabel(filter.operator)} ${filter.values}`
  }
  if (typeof filter.values === 'boolean') return filter.values ? 'Yes' : 'No'
  if ('amount' in filter.values) {
    return `Past ${filter.values.amount} ${filter.values.unit}`
  }
  if ('from' in filter.values) {
    return `${filter.values.from} – ${filter.values.to}`
  }
  return undefined
}

export function DataGridFilterIcon({
  kind,
  className,
}: {
  kind: DataGridColumnSchema['kind']
  className?: string
}) {
  const props = { 'aria-hidden': true as const, className }
  if (kind === 'text') return <Search {...props} />
  if (kind === 'category') return <Tags {...props} />
  if (kind === 'timestamp') return <Clock3 {...props} />
  return <Hash {...props} />
}

export function keepKeysOutOfMenu(event: KeyboardEvent<HTMLElement>) {
  if (event.key !== 'Escape' && event.key !== 'Tab') event.stopPropagation()
}

export function filterInputValue(
  value: unknown,
  part: 'first' | 'second',
): string {
  if (typeof value === 'string') {
    return part === 'first' ? value : ''
  }
  if (typeof value === 'number') return part === 'first' ? String(value) : ''
  if (!value || typeof value !== 'object' || Array.isArray(value)) return ''
  if ('from' in value && 'to' in value) {
    const candidate = part === 'first' ? value.from : value.to
    return typeof candidate === 'string' || typeof candidate === 'number' ? String(candidate) : ''
  }
  if ('amount' in value && 'unit' in value) {
    const candidate = part === 'first' ? value.amount : value.unit
    return typeof candidate === 'number' || typeof candidate === 'string'
      ? String(candidate)
      : ''
  }
  return ''
}

export function operatorLabel(operator: string): string {
  return operator.replace(/([A-Z])/g, ' $1').replace(/^./, (value) => value.toUpperCase())
}
